import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { RadixMultiSelect } from '@/components/radix';
import { t } from 'i18next';
// @ts-ignore - Service.js doesn't have type definitions
import { GetServiceGroup } from '@/utils/Api/Service';
import { usePlaygroundFormRegistry } from '../../hooks/usePlaygroundFormRegistry';
import {
    DURATION_OPERATORS_WITHIN_AFTER,
    DURATION_OPERATORS_WITHIN_BEFORE_AFTER,
} from '@/data/Marketing/marketingDurationOperators';
import DaySelector, { type DaySelectorLabel } from './DaySelector';
import {
    buildCompositeFromDaySelectorValue,
    DURATION_HOURS_TOUCHED,
    getDaySelectorValueFromComposite,
} from './durationPeriodUtils';

interface ActiveBookingServiceCompositeValues {
    serviceIds?: number[] | string[];
    days?: [number | null, number | null];
    operator?: string;
    selectedDurationFormat?: DaySelectorLabel;
    status?: ('BOOKED' | 'RESCHEDULED' | 'COMPLETED' | 'CANCELLED' | 'NOSHOW' | 'AUTOCOMPLETED')[] | 'CANCELLED'[];
}

interface ActiveBookingServiceInputProps {
    value: number[] | string[] | null | undefined;
    compositeValues?: ActiveBookingServiceCompositeValues;
    fixedStatus?: 'BOOKED' | 'RESCHEDULED' | 'COMPLETED' | 'CANCELLED' | 'NOSHOW' | 'AUTOCOMPLETED';
    /** When set, status multi-select is limited to these values (e.g. cancelled booking). */
    allowedStatuses?: Array<'BOOKED' | 'RESCHEDULED' | 'COMPLETED' | 'CANCELLED' | 'NOSHOW' | 'AUTOCOMPLETED'>;
    conditionInstanceId: string;
    renderId?: number;
}

const bookingStatusOptions = [
    { label: t('Calendar.Booked'), value: 'BOOKED' },
    { label: t('Calendar.Rescheduled'), value: 'RESCHEDULED' },
    { label: t('Common.Completed'), value: 'COMPLETED' },
    { label: t('Common.Cancelled'), value: 'CANCELLED' },
    { label: t('Calendar.AbsenceBooking'), value: 'NOSHOW' },
    { label: t('Common.AutoCompleted'), value: 'AUTOCOMPLETED' },
];

const createValidationSchema = (
    fixedStatus?: string,
    allowedStatuses?: string[],
) => {
    return Yup.object().shape({
        serviceIds: Yup.array().of(Yup.string()).min(1, t('Marketing.ServicesRequired')),
        duration: Yup.object().shape({
            hours: Yup.number().test(
                'required',
                t('Marketing.DaysRequired'),
                (val) => val !== null && val !== undefined,
            ),
        }),
        status: fixedStatus
            ? Yup.array().of(Yup.string().oneOf([fixedStatus])).min(1)
            : Yup.array()
                  .of(
                      Yup.string().oneOf(
                          allowedStatuses && allowedStatuses.length > 0
                              ? allowedStatuses
                              : [
                                    'BOOKED',
                                    'RESCHEDULED',
                                    'COMPLETED',
                                    'CANCELLED',
                                    'NOSHOW',
                                    'AUTOCOMPLETED',
                                ],
                      ),
                  )
                  .min(1, t('Marketing.StatusRequired')),
    });
};

const ActiveBookingServiceInput = ({
    compositeValues,
    fixedStatus,
    allowedStatuses,
    conditionInstanceId,
    renderId,
}: ActiveBookingServiceInputProps) => {
    const { register } = usePlaygroundFormRegistry();
    const [services, setServices] = useState<Array<{ label: string; value: string }>>([]);
    const [loading, setLoading] = useState(true);

    const serviceIds = compositeValues?.serviceIds || [];
    const isCancelled =
        fixedStatus === 'CANCELLED' ||
        renderId === 9 ||
        (allowedStatuses?.length === 2 &&
            allowedStatuses.includes('CANCELLED') &&
            allowedStatuses.includes('NOSHOW'));
    const initialDuration = getDaySelectorValueFromComposite(compositeValues, {
        includeOperator: true,
        defaultOperator: isCancelled ? 'AFTER_HOURS' : 'BEFORE_HOURS',
    });

    const statusFromComposite = compositeValues?.status
        ? Array.isArray(compositeValues.status)
            ? compositeValues.status
            : [compositeValues.status]
        : null;
    const defaultAllowedStatus = allowedStatuses?.[0] ? [...allowedStatuses] : ['BOOKED'];
    const status = fixedStatus
        ? [fixedStatus]
        : statusFromComposite || defaultAllowedStatus;

    const statusOptions =
        allowedStatuses && allowedStatuses.length > 0
            ? bookingStatusOptions.filter((option) => allowedStatuses.includes(option.value as (typeof allowedStatuses)[number]))
            : bookingStatusOptions;

    const formik = useFormik({
        initialValues: {
            serviceIds: Array.isArray(serviceIds) ? serviceIds.map(String) : [],
            duration: initialDuration,
            status: Array.isArray(status) ? status : [status],
        },
        validationSchema: createValidationSchema(fixedStatus, allowedStatuses),
        enableReinitialize: true,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: () => {},
    });

    useEffect(() => {
        const fetchServices = async () => {
            try {
                setLoading(true);
                const response = await GetServiceGroup();
                const serviceGroups = response?.data?.data || [];
                const allServices: Array<{ label: string; value: string }> = [];
                serviceGroups.forEach((group: { services?: Array<{ id: number | string; name?: string }> }) => {
                    if (group.services && Array.isArray(group.services)) {
                        group.services.forEach((service: { id: number | string; name?: string }) => {
                            allServices.push({
                                label: service.name || `Service ${service.id}`,
                                value: String(service.id),
                            });
                        });
                    }
                });
                setServices(allServices);
            } catch (error) {
                console.error('Error fetching services:', error);
                setServices([]);
            } finally {
                setLoading(false);
            }
        };
        fetchServices();
    }, []);

    useEffect(() => {
        register(conditionInstanceId, {
            validate: async () => {
                formik.setTouched({ serviceIds: true, duration: DURATION_HOURS_TOUCHED, status: true });
                const errors = await formik.validateForm();
                return Object.keys(errors).length === 0;
            },
            getValues: () => {
                const fallbackStatus =
                    allowedStatuses && allowedStatuses.length > 0 ? [...allowedStatuses] : ['BOOKED'];
                const result = {
                    serviceIds: formik.values.serviceIds.map(Number),
                    ...buildCompositeFromDaySelectorValue(formik.values.duration),
                    status: fixedStatus
                        ? [fixedStatus]
                        : Array.isArray(formik.values.status) && formik.values.status.length > 0
                          ? formik.values.status
                          : fallbackStatus,
                };
                return result;
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conditionInstanceId, formik.values, register, renderId, fixedStatus, allowedStatuses]);

    return (
        <div className="flex flex-col gap-4">
            <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('Marketing.SelectServices')}</label>
                <RadixMultiSelect
                    options={services}
                    selectedValues={new Set(formik.values.serviceIds)}
                    onSelectionChange={(selectedValues) => {
                        formik.setFieldValue('serviceIds', Array.from(selectedValues));
                    }}
                    placeholder={t('Marketing.SelectServices')}
                    textToDisplayWithCount={t('Marketing.ServicesSelected')}
                    className="w-full min-w-full"
                    disabled={loading}
                />
                {formik.touched.serviceIds && formik.errors.serviceIds && (
                    <div className="text-sm text-red-500 mt-1">
                        {typeof formik.errors.serviceIds === 'string'
                            ? formik.errors.serviceIds
                            : t('Marketing.ServicesRequired')}
                    </div>
                )}
            </div>

            {!fixedStatus && (
                <div className="w-full">
                    <label className="block text-sm font-medium text-text-primary mb-2">
                        {t('Marketing.BookingStatus')}
                    </label>
                    <RadixMultiSelect
                        options={statusOptions.map((status) => ({
                            label: status.label,
                            value: status.value,
                        }))}
                        selectedValues={
                            new Set(Array.isArray(formik.values.status) ? formik.values.status : [formik.values.status])
                        }
                        onSelectionChange={(selectedValues) => {
                            const statusArray = Array.from(selectedValues);
                            const fallback =
                                allowedStatuses && allowedStatuses.length > 0 ? [allowedStatuses[0]] : ['BOOKED'];
                            formik.setFieldValue('status', statusArray.length > 0 ? statusArray : fallback);
                        }}
                        placeholder={t('Marketing.SelectStatus')}
                        className="w-full min-w-full"
                    />
                    {formik.touched.status && formik.errors.status && (
                        <div className="text-sm text-error-500 mt-1">
                            {typeof formik.errors.status === 'string'
                                ? formik.errors.status
                                : t('Marketing.StatusRequired')}
                        </div>
                    )}
                </div>
            )}

            <DaySelector
                label={t('Marketing.SelectTimePeriod')}
                showOperator
                operatorOptions={
                    isCancelled ? DURATION_OPERATORS_WITHIN_AFTER : DURATION_OPERATORS_WITHIN_BEFORE_AFTER
                }
                value={formik.values.duration}
                onChange={(next) => formik.setFieldValue('duration', next)}
                summaryNoteKey={
                    isCancelled
                        ? 'Marketing.DaySelectorNoteCancelledBooking'
                        : 'Marketing.DaySelectorNoteActiveBooking'
                }
                showError={Boolean(
                    formik.touched.duration &&
                        (formik.errors.duration as { hours?: string } | undefined)?.hours,
                )}
                error={
                    typeof (formik.errors.duration as { hours?: string } | undefined)?.hours === 'string'
                        ? (formik.errors.duration as { hours?: string }).hours
                        : t('Marketing.DaysRequired')
                }
            />
        </div>
    );
};

export default ActiveBookingServiceInput;
